import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "superadmin" ? "/empresas" : "/consulta");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <img src="/zae-logo.png" alt="ZAE Clientes" className="h-9 w-9 rounded-sm object-contain" />
            <span className="font-heading font-bold tracking-tight text-xl">Clientes Corporativos Tomza</span>
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Iniciar sesión</h1>
          <p className="text-muted-foreground text-sm mb-8">
            Consulta la información de tus servicios administrados.
          </p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-caps">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                className="rounded-sm h-11 focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-caps">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm h-11 focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="login-password-input"
                required
              />
            </div>
            {error && (
              <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ingresar"}
            </Button>
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#09090B]">
        <img
          src="https://images.pexels.com/photos/33905748/pexels-photo-33905748.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Infraestructura industrial"
          className="absolute inset-0 w-full h-full object-cover opacity-80 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-[#09090B]/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="flex items-center gap-2 mb-3">
            <img src="/zae-logo.png" alt="ZAE Clientes" className="h-8 w-8 rounded-sm object-contain" />
            <span className="font-heading font-bold tracking-tight text-lg">Clientes Corporativos Tomza</span>
          </div>
          <p className="font-heading text-2xl font-bold leading-tight max-w-md">
            Control total de tus servicios administrados.
          </p>
        </div>
      </div>
    </div>
  );
}
